import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChartLine, FaLightbulb, FaCogs, FaUsers, FaArrowRight } from 'react-icons/fa';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { staggerContainer, staggerItem } from '../../utils/animations';
import { getCollection } from '../../firebase/firestore';
import './Services.css';

const Services = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    'chart': <FaChartLine />,
    'lightbulb': <FaLightbulb />,
    'cogs': <FaCogs />,
    'users': <FaUsers />
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { db } = await import('../../firebase/config');

      if (!db) {
        setServices([]);
        setLoading(false);
        return;
      }

      const data = await getCollection('services');
      const activeServices = data
        .filter(s => s.active)
        .sort((a, b) => a.order - b.order)
        .slice(0, 3)
        .map(s => ({
          id: s.id,
          slug: s.slug || s.id,
          icon: iconMap[s.icon] || <FaChartLine />,
          title: s.title,
          description: s.description,
          image: s.image || ''
        }));

      setServices(activeServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="services-section section" ref={ref}>
      <div className="container">
        <div className="section-title" style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h3 className="section-title-gradient">
            What We Do
          </h3>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Loading services...</p>
          </div>
        ) : services.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>No services available.</p>
          </div>
        ) : (
          <>
            <motion.div
              className="services-grid"
              variants={staggerContainer}
              initial="initial"
              animate={isVisible ? "animate" : "initial"}
            >
              {services.map((service, index) => (
                <Link
                  to={`/services/${service.slug}`}
                  key={index}
                  className="service-card-link"
                >
                  <motion.div
                    className="service-card"
                    variants={staggerItem}
                    whileHover={{ y: -10 }}
                  >
                    {service.image ? (
                      <div className="service-image">
                        <img src={service.image} alt={service.title} />
                      </div>
                    ) : (
                      <div className="service-icon">{service.icon}</div>
                    )}
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <div className="service-card-footer">
                      <span className="show-more-link">
                        Show More <FaArrowRight className="show-more-icon" />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </motion.div>

            <div className="services-cta">
              <Link to="/services" className="explore-services-btn">
                Explore Services
                <FaArrowRight className="btn-icon" />
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Services;
